import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';
import { SessionProvider } from '@/lib/session';
import { SoundProvider } from '@/features/sound/SoundProvider';
import { AppShell, PageShell } from './Shell';
import { Splash } from './Splash';
import { PlanScreen } from '@/features/plan/PlanScreen';
import { useSkin } from '@/lib/skin';

// Each feature owns its folder and exports its screens; heavy ones load on demand.
const LoginScreen = lazy(() => import('@/features/auth/LoginScreen'));
const ForgotScreen = lazy(() => import('@/features/auth/ForgotScreen'));
const ResetScreen = lazy(() => import('@/features/auth/ResetScreen'));
const TrackScreen = lazy(() => import('@/features/track/TrackScreen'));
const SoundsScreen = lazy(() => import('@/features/sounds/SoundsScreen'));
const LearnRoutes = lazy(() => import('@/features/learn/LearnRoutes'));
const ChatRoutes = lazy(() => import('@/features/chat/ChatRoutes'));
const OnboardingFlow = lazy(() => import('@/features/onboarding/OnboardingFlow'));
const SettingsRoutes = lazy(() => import('@/features/settings/SettingsRoutes'));
const InviteScreen = lazy(() => import('@/features/family/InviteScreen'));
const PurchaseReturn = lazy(() => import('@/features/settings/PurchaseReturn'));
// The app guide: its page and the runner that walks over the real screens.
const GuideHub = lazy(() => import('@/features/guide/GuideHub'));
const GuideRunner = lazy(() => import('@/features/guide/Runner'));
// Bold Blobs skin (alternative design): its own Today, Baby Button and check-in screens.
const TodayBlobs = lazy(() => import('@/themes/blobs/TodayBlobs').then((m) => ({ default: m.TodayBlobs })));
const SootheScreen = lazy(() => import('@/themes/blobs/SootheScreen'));
const CheckinBlobs = lazy(() => import('@/themes/blobs/CheckinBlobs'));
// Glass skin (light, liquid glass): one big button on its own home screen.
const GlassHome = lazy(() => import('@/themes/glass/GlassHome'));

function Home() {
  const skin = useSkin();
  if (skin === 'blobs') return <TodayBlobs />;
  if (skin === 'glass') return <GlassHome />;
  return <PlanScreen />;
}

export function App() {
  return (
    <SessionProvider>
      <SoundProvider>
        <BrowserRouter>
          <Suspense fallback={<Splash />}>
            <Routes>
              <Route element={<AppShell />}>
                <Route index element={<Home />} />
                <Route path="track/*" element={<TrackScreen />} />
                <Route path="sounds/*" element={<SoundsScreen />} />
                <Route path="learn/*" element={<LearnRoutes />} />
                <Route path="chat/*" element={<ChatRoutes />} />
              </Route>
              <Route element={<PageShell />}>
                <Route path="login" element={<LoginScreen />} />
                <Route path="forgot" element={<ForgotScreen />} />
                <Route path="reset-password" element={<ResetScreen />} />
                <Route path="welcome/*" element={<OnboardingFlow />} />
                <Route path="guide" element={<GuideHub />} />
                <Route path="invite/:token" element={<InviteScreen />} />
                <Route path="purchase/return" element={<PurchaseReturn />} />
                <Route path="soothe" element={<SootheScreen />} />
                <Route path="checkin" element={<CheckinBlobs />} />
              </Route>
              <Route element={<PageShell header />}>
                <Route path="settings/*" element={<SettingsRoutes />} />
              </Route>
              <Route path="*" element={<AppShell />} />
            </Routes>
          </Suspense>
          <Suspense fallback={null}><GuideRunner /></Suspense>
        </BrowserRouter>
      </SoundProvider>
    </SessionProvider>
  );
}
